import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TrackingProvider } from "@/components/providers/TrackingProvider";
import UrlNormalizer from "@/components/seo/UrlNormalizer";

const microSiteQueryClient = new QueryClient();
import { detectBrandProfile } from "@/lib/brandRegistry";
import { getMicroSiteContent } from "@/lib/microSiteContent";
import MicroSiteHome from "@/pages/microsite/MicroSiteHome";
import MicroSiteNotFound from "@/pages/microsite/MicroSiteNotFound";

const MicroSiteServices = lazy(() => import("@/pages/microsite/MicroSiteServices"));
const MicroSiteServiceDetail = lazy(() => import("@/pages/microsite/MicroSiteServiceDetail"));
const MicroSiteAbout = lazy(() => import("@/pages/microsite/MicroSiteAbout"));
const MicroSiteContact = lazy(() => import("@/pages/microsite/MicroSiteContact"));
const MicroSiteFAQ = lazy(() => import("@/pages/microsite/MicroSiteFAQ"));
const MicroSiteQuickEstimate = lazy(() => import("@/pages/microsite/MicroSiteQuickEstimate"));
const RoofVisualizer = lazy(() => import("@/pages/RoofVisualizer"));
const LeakDetection = lazy(() => import("@/pages/LeakDetection"));
const StormDamageAnalyzer = lazy(() => import("@/pages/StormDamageAnalyzer"));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading…</div>
  </div>
);

const MicroSiteApp = () => {
  const brand = detectBrandProfile();
  const content = getMicroSiteContent(brand.brand);
  const tools = content?.extraTools ?? [];

  return (
    <HelmetProvider>
      <QueryClientProvider client={microSiteQueryClient}>
        <BrowserRouter>
          <TrackingProvider>
            <UrlNormalizer />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<MicroSiteHome />} />
                <Route path="/services" element={<MicroSiteServices />} />
                <Route path="/services/:slug" element={<MicroSiteServiceDetail />} />
                <Route path="/about" element={<MicroSiteAbout />} />
                <Route path="/contact" element={<MicroSiteContact />} />
                <Route path="/faq" element={<MicroSiteFAQ />} />
                <Route path="/quick-estimate" element={<MicroSiteQuickEstimate />} />
                {tools.includes("roof-visualizer") && (
                  <Route path="/roof-visualizer" element={<RoofVisualizer />} />
                )}
                {tools.includes("leak-detection") && (
                  <Route path="/leak-detection" element={<LeakDetection />} />
                )}
                {tools.includes("storm-damage-analyzer") && (
                  <Route path="/storm-damage-analyzer" element={<StormDamageAnalyzer />} />
                )}
                <Route path="*" element={<MicroSiteNotFound />} />
              </Routes>
            </Suspense>
          </TrackingProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default MicroSiteApp;
